'use client';

import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import type {
  ControllerRenderProps,
  FieldValues,
  RegisterOptions
} from 'react-hook-form';

import { HelpCircleIcon } from 'lucide-react';

import { cn } from '@/utils/cn';
import { Label } from '@/components/shadcn/label';
import { Dialog, DialogContent } from '@/components/shadcn/dialog';
import {
  FormControl,
  FormDescription,
  FormField,
  FormFieldContext,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/shadcn/form';

/** What `Controller` hands a bound control. */
type FieldRenderProps = ControllerRenderProps<FieldValues, string>;

interface FieldProps {
  label: string;
  /**
   * Binds the control to this form field. `control` comes from the surrounding
   * `<Form>`, so it never needs passing. Omit `name` for a control that is not
   * part of a form — a filter or a picker — and drive it with `error` instead.
   */
  name?: string;
  /**
   * Validation for this field, in react-hook-form's `rules` shape. Forms with
   * a resolver describe validation in their schema instead.
   */
  rules?: RegisterOptions<FieldValues, string>;
  /**
   * Error text for fields that are not bound to react-hook-form. When `name`
   * is set the message comes from form state and this is ignored.
   *
   * A boolean only marks the field invalid — it renders no message. Callers
   * that pass one are relying on a sibling element to show the text.
   */
  error?: string | boolean;
  helpText?: string;
  /**
   * Keep the label for screen readers but take it out of the layout. For a
   * control whose surroundings already name it — a dialog titled after the one
   * field it edits — where a visible label would only repeat that name.
   */
  srOnlyLabel?: boolean;
  helpDialog?: React.ReactNode;
  rightComponent?: React.ReactNode;
  className?: string;
  /**
   * An element is handed `value` / `onChange` / `onBlur` / `ref` directly. A
   * control that names those differently — `Switch`, Radix `Select` — takes a
   * function instead: `{(field) => <Switch checked={field.value} …/>}`.
   */
  children?: React.ReactNode | ((field: FieldRenderProps) => React.ReactNode);
  /**
   * Skip the control wrapper that supplies `id` / `aria-*`. Use when the
   * children hold more than one control, or when a composite control needs
   * `<FormControl>` placed on its inner trigger (Radix `Select`, for example).
   */
  unwrapped?: boolean;
}

/**
 * Controls that ignore `value` / `onChange`, so cloning would leave them
 * uncontrolled without erroring. Keyed on displayName to avoid importing them.
 */
const NEEDS_RENDER_FN = new Set(['Switch', 'Select', 'SelectTrigger']);

/**
 * Hands `field` to the children: calls a function, or clones an element with
 * the standard `value` / `onChange` props.
 */
function bindChildren(
  children: FieldProps['children'],
  field: FieldRenderProps | undefined,
  name: string | undefined
): React.ReactNode {
  if (typeof children === 'function') {
    return field ? children(field) : null;
  }
  if (!field || !React.isValidElement(children)) {
    return children as React.ReactNode;
  }

  if (process.env.NODE_ENV !== 'production') {
    const type = children.type as { displayName?: string } | string;
    const displayName = typeof type === 'string' ? type : type?.displayName;
    if (displayName && NEEDS_RENDER_FN.has(displayName)) {
      console.error(
        `Field "${name}": <${displayName}> does not read value/onChange, so it ` +
          `will stay uncontrolled. Pass a function child instead: ` +
          `{(field) => <${displayName} … />}`
      );
    }
  }

  // `field.onBlur` marks the field touched, and a child may have its own
  // reason to know — so call both rather than letting one replace the other.
  const childProps = (children.props ?? {}) as {
    onBlur?: (event: unknown) => void;
  };
  const onBlur = childProps.onBlur
    ? (event: unknown) => {
        childProps.onBlur?.(event);
        field.onBlur();
      }
    : field.onBlur;

  return React.cloneElement(
    children as React.ReactElement<Partial<FieldRenderProps>>,
    { ...field, onBlur } as Partial<FieldRenderProps>
  );
}

// `space-y-0` overrides the `space-y-2` FormItem sets on the same element —
// stacked on `gap-2` it would double the spacing between label and control.
// `leading-5` and `pl-1` hold the label to the metrics the rest of the app uses,
// against the `leading-none` shadcn's Label applies.
const CONTAINER = 'flex w-full flex-col items-start gap-2 space-y-0';
const LABEL = 'flex w-full flex-row items-center pl-1 leading-5';
const MESSAGE = 'text-xs font-semibold';
// Help text and the error message both annotate the control, so they sit
// under it, closer to it than the container's `gap-2` would put them.
const ANNOTATIONS = 'flex w-full flex-col gap-1';
const HELP = 'pl-1 text-xs font-light text-muted-foreground';

/**
 * A control can only receive `id` and `aria-*` through `Slot` when it is a
 * single React element. Call sites that render several children keep their
 * previous markup instead of crashing `Slot`.
 */
function isSlottable(children: React.ReactNode): boolean {
  return React.Children.count(children) === 1 && React.isValidElement(children);
}

function LabelContent({
  label,
  helpDialog,
  onOpenHelp,
  rightComponent
}: {
  label: string;
  helpDialog?: React.ReactNode;
  onOpenHelp?: () => void;
  rightComponent?: React.ReactNode;
}) {
  return (
    <>
      {label}
      {helpDialog && (
        <button
          type="button"
          onClick={onOpenHelp}
          aria-label={`About ${label}`}
          className="ml-2 inline-flex"
        >
          <HelpCircleIcon className="size-4 text-zinc-400" />
        </button>
      )}
      {rightComponent && (
        <div className="ml-auto mt-auto pr-2">{rightComponent}</div>
      )}
    </>
  );
}

/** Renders `helpDialog` in a modal, with the trigger wired into the label row. */
function useHelpDialog(helpDialog: React.ReactNode) {
  const [open, setOpen] = React.useState(false);

  const dialog = helpDialog ? (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-3xl">{helpDialog}</DialogContent>
    </Dialog>
  ) : null;

  return { dialog, openHelp: () => setOpen(true) };
}

/**
 * Rendered inside `<FormItem>` so it can read the ids that `FormItem` generates.
 */
function BoundFieldBody({
  label,
  name,
  helpText,
  srOnlyLabel,
  helpDialog,
  rightComponent,
  children,
  unwrapped,
  field
}: FieldProps & { field?: FieldRenderProps }) {
  const { dialog, openHelp } = useHelpDialog(helpDialog);
  const bound = bindChildren(children, field, name);
  const wrap = !unwrapped && isSlottable(bound);

  return (
    <>
      <FormLabel className={srOnlyLabel ? 'sr-only' : LABEL}>
        <LabelContent
          label={label}
          helpDialog={helpDialog}
          onOpenHelp={openHelp}
          rightComponent={rightComponent}
        />
      </FormLabel>
      {wrap ? <FormControl>{bound}</FormControl> : bound}
      <div className={ANNOTATIONS}>
        {helpText && (
          <FormDescription className={HELP}>{helpText}</FormDescription>
        )}
        <FormMessage className={MESSAGE} />
      </div>
      {dialog}
    </>
  );
}

function BoundField({
  className,
  field,
  ...props
}: FieldProps & { field?: FieldRenderProps }) {
  return (
    <FormItem className={cn(CONTAINER, className)}>
      <BoundFieldBody {...props} field={field} />
    </FormItem>
  );
}

function PlainField({
  label,
  error,
  helpText,
  srOnlyLabel,
  helpDialog,
  rightComponent,
  className,
  children,
  unwrapped
}: FieldProps) {
  const id = React.useId();
  const controlId = `${id}-form-item`;
  const helpTextId = `${id}-form-item-description`;
  const messageId = `${id}-form-item-message`;
  const { dialog, openHelp } = useHelpDialog(helpDialog);
  // No field to hand out here, so a function child resolves to nothing.
  const content = bindChildren(children, undefined, undefined);
  const wrap = !unwrapped && isSlottable(content);
  const message = typeof error === 'string' ? error : undefined;

  return (
    <div className={cn(CONTAINER, className)}>
      <Label
        htmlFor={wrap ? controlId : undefined}
        className={srOnlyLabel ? 'sr-only' : LABEL}
      >
        <LabelContent
          label={label}
          helpDialog={helpDialog}
          onOpenHelp={openHelp}
          rightComponent={rightComponent}
        />
      </Label>

      {wrap ? (
        <Slot
          id={controlId}
          aria-invalid={!!error}
          aria-describedby={message ? `${helpTextId} ${messageId}` : helpTextId}
        >
          {content}
        </Slot>
      ) : (
        content
      )}

      <div className={ANNOTATIONS}>
        {helpText && (
          <p id={helpTextId} className={HELP}>
            {helpText}
          </p>
        )}
        {message && (
          <p id={messageId} className={cn(MESSAGE, 'text-destructive')}>
            {message}
          </p>
        )}
      </div>
      {dialog}
    </div>
  );
}

/**
 * Label, help text and error message for one control.
 *
 * Give it a `name` and it binds to that form field: the control is wired to
 * form state and `htmlFor`, `aria-invalid`, `aria-describedby` and the error
 * message all follow from it. Without a `name` the same markup is driven by
 * the `error` prop, for controls that are not part of a form.
 */
export const Field: React.FC<FieldProps> = (props) => {
  const fieldContext = React.useContext(FormFieldContext);

  // Binds itself. `control` resolves from the surrounding <Form>.
  if (props.name) {
    return (
      <FormField
        name={props.name}
        rules={props.rules}
        render={({ field }) => <BoundField {...props} field={field} />}
      />
    );
  }

  // Already inside a <FormField> a caller opened themselves.
  return fieldContext?.name ? (
    <BoundField {...props} />
  ) : (
    <PlainField {...props} />
  );
};

Field.displayName = 'Field';
